import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["waffleGraph", "waffleContainer", "expandIcon", "compressIcon", "summaryCards", "headerRow", "paginationFooter"]

  connect() {
    this.isFullscreen = false
    this.originalParent = null
    this.originalMaxHeight = null
    this.originalClasses = null
    this.handleKeydown = this.handleKeydown.bind(this)
    this.handleFrameLoad = this.handleFrameLoad.bind(this)
  }

  toggle(event) {
    // Prevent any default behavior and stop propagation
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    if (this.isFullscreen) {
      this.exitFullscreen()
    } else {
      if (!this.hasWaffleGraphTarget || !this.hasWaffleContainerTarget) {
        console.warn('Required targets not available for entering fullscreen')
        return
      }
      this.enterFullscreen()
    }
  }

  enterFullscreen() {
    // Store original state
    this.originalParent = this.waffleGraphTarget.parentNode
    this.originalClasses = this.waffleGraphTarget.className

    // Store references before moving DOM (targets become unreachable after move)
    this.waffleGraphElement = this.waffleGraphTarget
    this.expandIconElements = this.expandIconTargets
    this.compressIconElements = this.compressIconTargets
    if (this.hasSummaryCardsTarget) {
      this.summaryCardsElement = this.summaryCardsTarget
      this.summaryCardsElement.classList.add('hidden')
    }

    // Find the turbo frame inside the wrapper for frame load events
    this.turboFrameElement = this.waffleGraphElement.querySelector('turbo-frame')
    if (this.turboFrameElement) {
      this.turboFrameElement.addEventListener('turbo:frame-load', this.handleFrameLoad)
    }

    // Apply fullscreen styles to waffle container
    this.applyFullscreenToWaffleContainer()

    // Apply sticky header
    this.applyFullscreenToHeaderRow()

    // Create fullscreen overlay
    const overlay = document.createElement('div')
    overlay.className = 'fixed inset-0 bg-white z-[9999] flex flex-col overflow-hidden'
    overlay.id = 'fullscreen-overlay'

    // Move wrapper to overlay
    this.waffleGraphElement.className = 'flex-1 bg-white pt-4 px-4 pb-1 flex flex-col min-h-0'
    overlay.appendChild(this.waffleGraphElement)
    document.body.appendChild(overlay)

    // Update button states
    this.expandIconElements.forEach(el => el.classList.add('hidden'))
    this.compressIconElements.forEach(el => el.classList.remove('hidden'))

    this.isFullscreen = true

    // Add keyboard listener
    document.addEventListener('keydown', this.handleKeydown)

    // Prevent body scroll
    document.body.style.overflow = 'hidden'
  }

  exitFullscreen() {
    const overlay = document.getElementById('fullscreen-overlay')
    if (overlay && this.waffleGraphElement && this.originalParent) {
      // Restore wrapper to original position
      this.waffleGraphElement.className = this.originalClasses
      this.originalParent.appendChild(this.waffleGraphElement)
      overlay.remove()
    }

    // Restore turbo-frame styles
    if (this.turboFrameElement) {
      this.turboFrameElement.style.display = ''
      this.turboFrameElement.style.flexDirection = ''
      this.turboFrameElement.style.flex = ''
      this.turboFrameElement.style.minHeight = ''
    }

    // Restore waffle container styles
    const waffleContainer = this.waffleGraphElement?.querySelector('[data-fullscreen-toggle-target="waffleContainer"]')
    if (waffleContainer) {
      waffleContainer.style.maxHeight = ''
      waffleContainer.style.overflow = ''
      waffleContainer.style.flex = ''
      waffleContainer.style.minHeight = ''
    }

    // Restore header row
    const headerRow = this.waffleGraphElement?.querySelector('[data-fullscreen-toggle-target="headerRow"]')
    if (headerRow) {
      headerRow.classList.remove('sticky', 'top-0', 'z-30')
    }

    // Update button states
    if (this.expandIconElements) {
      this.expandIconElements.forEach(el => el.classList.remove('hidden'))
    }
    if (this.compressIconElements) {
      this.compressIconElements.forEach(el => el.classList.add('hidden'))
    }

    // Restore summary cards
    if (this.summaryCardsElement) {
      this.summaryCardsElement.classList.remove('hidden')
    }

    this.isFullscreen = false

    // Remove listeners
    document.removeEventListener('keydown', this.handleKeydown)
    if (this.turboFrameElement) {
      this.turboFrameElement.removeEventListener('turbo:frame-load', this.handleFrameLoad)
    }

    // Restore body scroll
    document.body.style.overflow = ''
  }

  handleFrameLoad() {
    if (!this.isFullscreen) return

    // Re-apply fullscreen styles to new waffle container (replaced by turbo frame)
    this.applyFullscreenToWaffleContainer()
    this.applyFullscreenToHeaderRow()
  }

  applyFullscreenToWaffleContainer() {
    // Make the turbo-frame element a flex container so sticky footer works
    if (this.turboFrameElement) {
      this.turboFrameElement.style.display = 'flex'
      this.turboFrameElement.style.flexDirection = 'column'
      this.turboFrameElement.style.flex = '1'
      this.turboFrameElement.style.minHeight = '0'
    }

    const container = this.waffleGraphElement.querySelector('[data-fullscreen-toggle-target="waffleContainer"]')
    if (container) {
      container.style.maxHeight = 'none'
      container.style.overflow = 'auto'
      container.style.flex = '1'
      container.style.minHeight = '0'
    }
  }

  applyFullscreenToHeaderRow() {
    const headerRow = this.waffleGraphElement.querySelector('[data-fullscreen-toggle-target="headerRow"]')
    if (headerRow) {
      headerRow.classList.add('sticky', 'top-0', 'z-30')
    }
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.isFullscreen) {
      event.preventDefault()
      this.exitFullscreen()
    }
  }

  disconnect() {
    if (this.isFullscreen) {
      this.exitFullscreen()
    }
    document.removeEventListener('keydown', this.handleKeydown)
  }
}
